import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, LogIn, UserPlus } from 'lucide-react';
import { ApiError } from '@/lib/apiClient';
import { useAuth } from '@/hooks/useAuth';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [cid, setCid] = useState('');
  const [username, setUsername] = useState(import.meta.env.VITE_DEFAULT_USERNAME ?? '');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<'login' | 'register' | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function submit(kind: 'login' | 'register'): Promise<void> {
    setError(null);
    setPending(kind);
    try {
      const payload = {
        cid: cid.trim(),
        username: username.trim(),
        password: password.trim(),
      };
      if (kind === 'login') await login(payload);
      else await register(payload);
      navigate('/devices', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(null);
    }
  }

  const missingFields = !cid.trim() || !username.trim() || !password.trim();
  const authError = error instanceof ApiError ? error : null;

  return (
    <div className="flex min-h-full items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-lg bg-foreground text-background">
            <Activity className="h-6 w-6" aria-hidden />
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">MyTag</h1>
            <p className="text-sm text-muted-foreground">Accédez à vos appareils.</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Connexion</CardTitle>
            <CardDescription>Renseignez vos identifiants pour continuer.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void submit('login');
              }}
            >
              <Field
                label="CID"
                htmlFor="cid"
                hint="Identifiant client attribué par votre fournisseur. Ce n’est pas le numéro de l’appareil."
              >
                <Input
                  id="cid"
                  value={cid}
                  onChange={(event) => setCid(event.target.value)}
                  placeholder="CID fourni par le fournisseur"
                  autoComplete="off"
                  spellCheck={false}
                  required
                />
              </Field>

              <Field label="Nom d'utilisateur" htmlFor="username">
                <Input
                  id="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="demo"
                  autoComplete="username"
                  required
                />
              </Field>

              <Field label="Mot de passe" htmlFor="password">
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </Field>

              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  type="submit"
                  loading={pending === 'login'}
                  disabled={missingFields || pending !== null}
                  icon={<LogIn className="h-4 w-4" />}
                >
                  Se connecter
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  loading={pending === 'register'}
                  disabled={missingFields || pending !== null}
                  icon={<UserPlus className="h-4 w-4" />}
                  onClick={() => void submit('register')}
                >
                  Créer un compte
                </Button>
              </div>
            </form>

            {error ? (
              <ErrorState
                error={error}
                title={authError?.isAuthError ? 'Identifiants refusés' : 'Connexion impossible'}
              />
            ) : null}

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
